import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  AlertTriangle,
  Lightbulb,
  Map,
  MessageSquare,
  Pencil,
  Plus,
  ThumbsUp,
  Wrench,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  featureRequestCategoryLabel,
  FEATURE_REQUEST_STATUSES,
} from "../constants/featureRequests";
import { useFeatureRequestTopics } from "../hooks/useFeatureRequestTopics";
import { useFeatureRequestTeams } from "../hooks/useFeatureRequestTeams";
import FeatureRequestManagePanel from "../components/FeatureRequestManagePanel";
import { apiService, extractApiError } from "../services/api";
import type {
  FeatureRequest,
  FeatureRequestCategory,
  FeatureRequestStatus,
} from "../types";
import { useAuth } from "../hooks/useAuth";
import LoadingSpinner from "../components/LoadingSpinner";
import MessageCard from "../components/MessageCard";
import FeatureRequestForm from "../components/FeatureRequestForm";
import FeatureRequestDetailModal from "../components/FeatureRequestDetailModal";

const BOARD_COLUMNS: {
  status: FeatureRequestStatus;
  label: string;
  headerClasses: string;
}[] = FEATURE_REQUEST_STATUSES.map((s) => ({
  status: s.value,
  label: s.label,
  headerClasses: s.badgeClasses,
}));

export function cycleCategory(
  category: FeatureRequestCategory,
  included: FeatureRequestCategory[],
  excluded: FeatureRequestCategory[],
): { included: FeatureRequestCategory[]; excluded: FeatureRequestCategory[] } {
  if (included.includes(category)) {
    return {
      included: included.filter((c) => c !== category),
      excluded: [...excluded, category],
    };
  }
  if (excluded.includes(category)) {
    return { included, excluded: excluded.filter((c) => c !== category) };
  }
  return { included: [...included, category], excluded };
}

export function isCategoryVisible(
  category: FeatureRequestCategory,
  included: FeatureRequestCategory[],
  excluded: FeatureRequestCategory[],
): boolean {
  return (
    (included.length === 0 || included.includes(category)) &&
    !excluded.includes(category)
  );
}

interface FeatureRequestListData {
  items: FeatureRequest[];
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

type FeatureRequestTab = "add" | "board" | "manage";

const TABS: {
  id: FeatureRequestTab;
  label: string;
  icon: typeof Plus;
}[] = [
  { id: "board", label: "Feature requests", icon: Map },
  { id: "add", label: "Add a request", icon: Plus },
];

const MANAGE_TAB = { id: "manage" as const, label: "Manage", icon: Wrench };

// Column -> the board status an admin may move a request to. These match
// one-to-one, which is what makes dropping a card unambiguous.
const BOARD_STATUS_BY_COLUMN: Record<string, string> = {
  submitted: "Submitted",
  planned: "Planned",
  "in-progress": "In Progress",
  blocked: "Blocked",
  done: "Done",
  declined: "Declined",
};

interface FeatureRequestCardProps {
  request: FeatureRequest;
  onOpen: () => void;
  onVote: () => void;
  voteDisabled: boolean;
  onEdit?: (() => void) | undefined;
  draggable?: boolean;
  onDragStart?: (() => void) | undefined;
  onDragEnd?: (() => void) | undefined;
  dragging?: boolean;
}

function FeatureRequestCard({
  request,
  onOpen,
  onVote,
  voteDisabled,
  onEdit,
  draggable = false,
  onDragStart,
  onDragEnd,
  dragging = false,
}: FeatureRequestCardProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", request.id);
        // Deferred: updating state here re-renders the card while Chromium is
        // still initiating the drag, and mutating the dragged element at that
        // point makes the browser cancel the drag outright.
        window.setTimeout(() => onDragStart?.(), 0);
      }}
      onDragEnd={() => onDragEnd?.()}
      className={`card w-full text-left transition-shadow hover:shadow-md ${
        draggable ? "cursor-grab active:cursor-grabbing" : ""
      } ${dragging ? "opacity-40" : ""}`}
    >
      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
        {request.title}
      </h3>
      {request.description && (
        <p className="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
          {request.description}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-gray-700 dark:text-gray-200">
          {featureRequestCategoryLabel(request.category)}
        </span>
        {request.etaQuarter && (
          <span className="inline-flex items-center rounded-full bg-primary-100 dark:bg-primary-900 px-2 py-0.5 text-primary-800 dark:text-primary-200">
            ETA {request.etaQuarter}
          </span>
        )}
      </div>
      <div className="mt-3 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
        <span
          role="button"
          tabIndex={0}
          aria-label={request.hasVoted ? "Remove vote" : "Vote"}
          onClick={(e) => {
            e.stopPropagation();
            if (!voteDisabled) onVote();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              e.preventDefault();
              if (!voteDisabled) onVote();
            }
          }}
          className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 ${
            request.hasVoted
              ? "text-primary-600 dark:text-primary-400 font-medium"
              : ""
          }`}
        >
          <ThumbsUp
            className={`h-3.5 w-3.5 ${request.hasVoted ? "fill-current" : ""}`}
          />
          {request.voteCount}
        </span>
        <span className="inline-flex items-center gap-1">
          <MessageSquare className="h-3.5 w-3.5" />
          {request.commentCount}
        </span>
        {onEdit && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Edit feature request"
            title="Edit feature request"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                e.preventDefault();
                onEdit();
              }
            }}
            className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Pencil className="h-3.5 w-3.5" />
          </span>
        )}
      </div>
    </button>
  );
}

export default function FeatureRequestsPage() {
  const { user } = useAuth();
  const [modalRequest, setModalRequest] = useState<FeatureRequest | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("request");
  const setSelectedId = (id: string | null) =>
    setSearchParams(
      (prev) => {
        if (id) prev.set("request", id);
        else prev.delete("request");
        return prev;
      },
      { replace: true },
    );
  const [activeTab, setActiveTab] = useState<FeatureRequestTab>("board");
  const [categoryFilter, setCategoryFilter] = useState<
    FeatureRequestCategory[]
  >([]);
  const [excludedCategories, setExcludedCategories] = useState<
    FeatureRequestCategory[]
  >([]);
  const queryClient = useQueryClient();

  const requestsQuery = useQuery<FeatureRequestListData>(
    "feature-requests",
    async () => {
      const res = await apiService.getFeatureRequests();
      return {
        items: (res.data?.data?.items ?? []) as FeatureRequest[],
        isAdmin: Boolean(res.data?.data?.isAdmin),
        isSuperAdmin: Boolean(res.data?.data?.isSuperAdmin),
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const voteMutation = useMutation(
    (id: string) => apiService.toggleFeatureRequestVote(id),
    {
      onSuccess: () => queryClient.invalidateQueries("feature-requests"),
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );

  const cycleCategoryFilter = (category: FeatureRequestCategory) => {
    const next = cycleCategory(category, categoryFilter, excludedCategories);
    setCategoryFilter(next.included);
    setExcludedCategories(next.excluded);
  };

  const items = requestsQuery.data?.items ?? [];
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const moveMutation = useMutation(
    ({ id, status }: { id: string; status: string }) =>
      apiService.setFeatureRequestStatus(id, status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("feature-requests");
        toast.success("Status updated");
      },
      onError: (err) => {
        toast.error(extractApiError(err));
      },
    },
  );

  const isAdmin = requestsQuery.data?.isAdmin ?? false;
  const { topics } = useFeatureRequestTopics();
  const { teams } = useFeatureRequestTeams();
  const [teamFilter, setTeamFilter] = useState<string>("");
  const visibleTabs = isAdmin ? [...TABS, MANAGE_TAB] : TABS;

  // A team filter selects every topic that team owns.
  const teamTopicSlugs =
    teamFilter === ""
      ? null
      : new Set(teams.find((t) => t.id === teamFilter)?.topics ?? []);
  const selectedRequest = items.find((r) => r.id === selectedId) ?? null;
  const visibleItems = items.filter(
    (request) =>
      isCategoryVisible(request.category, categoryFilter, excludedCategories) &&
      (teamTopicSlugs === null || teamTopicSlugs.has(request.category)),
  );

  const canEdit = (request: FeatureRequest) =>
    Boolean(user?.email) &&
    request.requesterEmail.toLowerCase() === user?.email?.toLowerCase();

  const closeModal = () => setModalRequest(null);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Feature Requests
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-300">
            Suggest improvements to DevXHub and the platform tooling, vote on
            ideas, and follow what happens with them.
          </p>
        </div>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6" aria-label="Feature request views">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                aria-current={isActive ? "page" : undefined}
                className={`inline-flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                  isActive
                    ? "border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === "manage" && isAdmin && <FeatureRequestManagePanel />}

      {activeTab === "add" && (
        <>
          <MessageCard icon={Wrench} title="Feature requests only" tone="info">
            This form is for feature requests and improvement ideas. If
            something is broken or you need support, please raise it through the{" "}
            <a
              href="https://serviceportal.sandvik.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 dark:text-primary-400 underline"
            >
              ServicePortal
            </a>{" "}
            instead.
          </MessageCard>

          <FeatureRequestForm />
        </>
      )}

      {activeTab === "board" && (
        <>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Board
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Statuses are synced with the linked GitHub issues. Click a card
              for details and discussion.
            </p>
          </div>

          {teams.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-12 shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">
                Team
              </span>
              <button
                type="button"
                onClick={() => setTeamFilter("")}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  teamFilter === ""
                    ? "bg-primary-600 text-white"
                    : "border border-gray-300 text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
              >
                All teams
              </button>
              {teams.map((team) => (
                <button
                  key={team.id}
                  type="button"
                  onClick={() => setTeamFilter(team.id)}
                  aria-pressed={teamFilter === team.id}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    teamFilter === team.id
                      ? "bg-primary-600 text-white"
                      : "border border-gray-300 text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  }`}
                >
                  {team.name}
                </button>
              ))}
            </div>
          )}

          <div
            className="flex flex-wrap items-center gap-2"
            role="group"
            aria-label="Filter by category"
          >
            <span className="w-12 shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">
              Topic
            </span>
            <button
              type="button"
              onClick={() => {
                setCategoryFilter([]);
                setExcludedCategories([]);
              }}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                categoryFilter.length === 0 && excludedCategories.length === 0
                  ? "bg-primary-600 text-white"
                  : "border border-gray-300 text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
            >
              All
            </button>
            {topics.map((option) => {
              const active = categoryFilter.includes(option.slug);
              const excluded = excludedCategories.includes(option.slug);
              return (
                <button
                  key={option.slug}
                  type="button"
                  onClick={() => cycleCategoryFilter(option.slug)}
                  aria-pressed={active}
                  title="Click to include, click again to exclude, again to clear"
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    active
                      ? "bg-primary-600 text-white"
                      : excluded
                        ? "border border-red-500 text-red-500 line-through hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
                        : "border border-gray-300 text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          {requestsQuery.isLoading && (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
            </div>
          )}

          {requestsQuery.isError && (
            <MessageCard
              icon={AlertTriangle}
              title="Could not load feature requests"
              tone="danger"
            >
              Something went wrong while loading the list. Please try again
              later.
            </MessageCard>
          )}

          {requestsQuery.data && items.length === 0 && (
            <MessageCard icon={Lightbulb} title="No feature requests yet">
              Be the first to suggest an improvement from the “Add a request”
              tab.
            </MessageCard>
          )}

          {requestsQuery.data && items.length > 0 && (
            <div className="overflow-x-auto pb-2">
              <div className="flex gap-4 min-w-max">
                {BOARD_COLUMNS.map((column) => {
                  const columnItems = visibleItems
                    .filter((request) => request.status === column.status)
                    .sort((a, b) => b.voteCount - a.voteCount);
                  const boardStatus = BOARD_STATUS_BY_COLUMN[column.status];
                  const dragged = draggingId
                    ? items.find((r) => r.id === draggingId)
                    : null;
                  // Only offer a drop where the move is actually permitted and
                  // would change something.
                  const droppable = Boolean(
                    dragged?.canManage &&
                      boardStatus &&
                      dragged.status !== column.status,
                  );
                  return (
                    <div
                      key={column.status}
                      className={`w-72 flex-shrink-0 rounded-lg transition-colors ${
                        dropTarget === column.status
                          ? "bg-primary-50 ring-2 ring-primary-400 dark:bg-primary-950"
                          : ""
                      }`}
                      onDragOver={(e) => {
                        if (!droppable) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        setDropTarget(column.status);
                      }}
                      onDragLeave={(e) => {
                        if (e.currentTarget.contains(e.relatedTarget as Node))
                          return;
                        setDropTarget((t) => (t === column.status ? null : t));
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDropTarget(null);
                        const id = e.dataTransfer.getData("text/plain");
                        if (droppable && id && boardStatus) {
                          moveMutation.mutate({ id, status: boardStatus });
                        }
                      }}
                    >
                      <div
                        className={`rounded-md px-3 py-1.5 text-sm font-medium ${column.headerClasses}`}
                      >
                        {column.label}{" "}
                        <span className="opacity-70">
                          ({columnItems.length})
                        </span>
                      </div>
                      <div className="mt-3 space-y-3">
                        {columnItems.map((request) => (
                          <FeatureRequestCard
                            key={request.id}
                            request={request}
                            onOpen={() => setSelectedId(request.id)}
                            onVote={() => voteMutation.mutate(request.id)}
                            voteDisabled={voteMutation.isLoading}
                            onEdit={
                              canEdit(request)
                                ? () => setModalRequest(request)
                                : undefined
                            }
                            draggable={request.canManage}
                            dragging={draggingId === request.id}
                            onDragStart={() => setDraggingId(request.id)}
                            onDragEnd={() => {
                              setDraggingId(null);
                              setDropTarget(null);
                            }}
                          />
                        ))}
                        {columnItems.length === 0 && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 px-1">
                            Nothing here yet.
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {modalRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex w-full max-w-xl flex-col rounded-lg bg-white shadow-xl dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Edit feature request
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <FeatureRequestForm
                request={modalRequest}
                onSuccess={closeModal}
                onCancel={closeModal}
              />
            </div>
          </div>
        </div>
      )}

      {selectedRequest && (
        <FeatureRequestDetailModal
          request={selectedRequest}
          isAdmin={selectedRequest?.canManage ?? false}
          otherRequests={items.filter((r) => r.id !== selectedRequest.id)}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
